import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gem, Bomb, Rocket, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

export default function CrashMinesGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [mines, setMines] = useState(3);
  const [round, setRound] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [bombs, setBombs] = useState([]);
  const [gem, setGem] = useState(1);
  const [combined, setCombined] = useState(0);
  const [potential, setPotential] = useState(0);
  const [crashMult, setCrashMult] = useState(1);
  const [over, setOver] = useState(false);
  const startRef = useRef(0);
  const growthRef = useRef({ b: 0.35, c: 0.03 });
  const rafRef = useRef(null);
  const peekRef = useRef(null);

  useEffect(() => () => { cancelAnimationFrame(rafRef.current); clearInterval(peekRef.current); }, []);

  const tick = () => {
    const t = (performance.now() - startRef.current) / 1000;
    const g = growthRef.current;
    setCrashMult(parseFloat((1 + g.b * t + g.c * t * t).toFixed(2)));
    rafRef.current = requestAnimationFrame(tick);
  };

  const start = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    try {
      const { data } = await api.post("/hybrid/crashmines/start", { bet, mines });
      setRound(data); setRevealed({}); setBombs([]); setGem(1); setCombined(0); setPotential(0); setCrashMult(1); setOver(false);
      setUser((u) => ({ ...u, coins: data.coins }));
      startRef.current = performance.now();
      growthRef.current = data.growth;
      rafRef.current = requestAnimationFrame(tick);
      peekRef.current = setInterval(pollPeek, 300);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const stopLoops = () => { cancelAnimationFrame(rafRef.current); clearInterval(peekRef.current); };

  const pollPeek = async () => {
    if (!round) return;
    try {
      const { data } = await api.get(`/hybrid/crashmines/${roundIdRef.current}/peek`);
      if (data.busted) endBust(data, "crashed");
    } catch { /* ignore */ }
  };
  const roundIdRef = useRef(null);
  useEffect(() => { roundIdRef.current = round?.round_id || null; }, [round]);

  const endBust = (data, reason) => {
    stopLoops();
    setBombs(data.mine_positions || []);
    setOver(true);
    if (data.coins != null) setUser((u) => ({ ...u, coins: data.coins }));
    toast.error(reason === "mine" ? "💣 Hit a mine!" : "🚀 Crashed!");
    react({ outcome: "loss", game: "crashmines" });
    setTimeout(() => setRound(null), 1900);
  };

  const reveal = async (tile) => {
    if (!round || over || revealed[tile] != null) return;
    try {
      const { data } = await api.post("/hybrid/crashmines/reveal", { round_id: round.round_id, tile });
      if (data.busted) return endBust(data, data.reason);
      sfx("correct");
      setRevealed((r) => ({ ...r, [tile]: "gem" }));
      setGem(data.gem_mult); setCombined(data.combined); setPotential(data.potential_payout);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cashout = async () => {
    if (!round || over) return;
    try {
      const { data } = await api.post("/hybrid/crashmines/cashout", { round_id: round.round_id });
      if (data.busted) return endBust(data, data.reason);
      stopLoops();
      setBombs(data.mine_positions || []); setOver(true);
      setUser((u) => ({ ...u, coins: data.coins }));
      toast.success(`${data.combined}x · +${data.payout.toLocaleString()} coins`);
      react({ outcome: "win", game: "crashmines", big: data.combined >= 5 });
      setTimeout(() => setRound(null), 1900);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const active = round && !over;

  return (
    <GameFrame title="Crash Mines" subtitle="PhonePlay signature — stack gems while the rocket climbs. Lock in before a mine or the crash."
      side={<>
        {active ? (
          <button data-testid="crashmines-cashout" onClick={cashout} disabled={Object.keys(revealed).length === 0}
            className="w-full py-5 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-black text-lg hover:opacity-90 transition-opacity duration-200 disabled:opacity-40 pulse-ring">
            LOCK IN · {potential.toLocaleString()} ({combined}x)
          </button>
        ) : (
          <div className="glass rounded-2xl p-5 border border-white/10">
            <label className="text-sm text-gray-400 mb-2 block">Mines: {mines}</label>
            <input type="range" min={1} max={20} value={mines} onChange={(e) => setMines(parseInt(e.target.value))} className="w-full mb-4 accent-amber-500" data-testid="crashmines-count" />
            <BetControls bet={bet} setBet={setBet} coins={user?.coins} onPlay={start} playLabel="LAUNCH" testid="crashmines-start" />
          </div>
        )}
        <div className="glass rounded-2xl p-5 border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400 flex items-center gap-1.5"><Rocket className="w-4 h-4 text-amber-400" /> Crash</span>
            <span className={`font-mono font-black text-xl ${active ? "text-amber-300" : "text-gray-500"}`}>{crashMult.toFixed(2)}x</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400 flex items-center gap-1.5"><Gem className="w-4 h-4 text-emerald-400" /> Gems</span>
            <span className="font-mono font-black text-xl text-emerald-300">{gem.toFixed(2)}x</span>
          </div>
        </div>
        <div className="glass rounded-2xl p-5 border border-white/10 text-sm text-gray-400">
          <p className="font-semibold text-white mb-1 flex items-center gap-2"><Sparkles className="w-4 h-4 text-fuchsia-400" /> The blend</p>
          Your win is Gems × Crash. Keep revealing to grow gems while the rocket climbs — but bank it before a mine or the bust. Coins only.
        </div>
      </>}>
      <div className="glass rounded-3xl p-6 border border-amber-500/20 glow-coins">
        <div className="text-center mb-5">
          <span className="font-mono font-black text-5xl" style={{ color: over ? "#EF4444" : "#F59E0B" }}>{crashMult.toFixed(2)}x</span>
          <span className="text-gray-500 mx-3">×</span>
          <span className="font-mono font-black text-5xl text-emerald-400">{gem.toFixed(2)}x</span>
        </div>
        <div className="grid grid-cols-5 gap-2.5 max-w-md mx-auto">
          {Array.from({ length: 25 }).map((_, tile) => {
            const isGem = revealed[tile] === "gem";
            const isBomb = bombs.includes(tile);
            return (
              <motion.button key={tile} data-testid={`crashmines-tile-${tile}`} onClick={() => reveal(tile)} disabled={!active}
                whileTap={{ scale: 0.92 }}
                className={`aspect-square rounded-xl flex items-center justify-center transition-colors duration-200 border
                  ${isGem ? "bg-emerald-500/25 border-emerald-500/50" : isBomb ? "bg-red-500/25 border-red-500/50" : "bg-black/40 border-white/10 hover:bg-white/5"}`}>
                {isGem && <Gem className="w-6 h-6 text-emerald-400" />}
                {isBomb && !isGem && <Bomb className="w-6 h-6 text-red-400" />}
              </motion.button>
            );
          })}
        </div>
      </div>
    </GameFrame>
  );
}
